// The Excursion investigation workflow's runs at the replay cursor, read the
// same way on every surface that lists them outside the flow landing (the
// Workflows catalogue, K15 / K19). Glue only: it gathers the committed K6
// trace, the session's runs and the FMEA approvals, and hands them to the
// pure `investigationRunRows` projection.

import { useMemo } from 'react';
import type { SeriesTime } from '@/versions/v1/schema/reading-series';
import { useAdvisory } from '@/versions/v1/shared/AdvisoryProvider';
import { useScenario } from '@/versions/v1/shared/ScenarioProvider';
import { useAgentTrace } from '@/versions/v1/shared/useAgentTrace';
import { fmeaActionApproval } from '@/versions/v1/shared/FmeaLearningEngine';
import { CASES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import {
  investigationRunRows,
  investigationToolList,
  recordedRunForTrace,
  type InvestigationRunRow,
} from '@/versions/v1/modules/advisory/InvestigationRun.transform';

export function useInvestigationRunRows(): {
  runs: InvestigationRunRow[];
  /** The read-only tools the recording used, `null` until the trace loads. */
  tools: string | null;
  /** False while the committed recording is still loading: the runs list is not yet complete. */
  ready: boolean;
} {
  const advisory = useAdvisory();
  const { state: scenario } = useScenario();
  const recordedCase = CASES.find((row) => row.analysisRun?.fixture);
  const traceState = useAgentTrace(recordedCase?.analysisRun?.fixture);

  const runs = useMemo(() => {
    const recorded = traceState.status === 'ready' ? recordedRunForTrace(CASES, traceState.trace) : null;
    const approvedAtByCase = new Map<string, SeriesTime>();
    for (const run of [...advisory.investigationRuns, ...(recorded ? [recorded] : [])]) {
      const approval = fmeaActionApproval(advisory.fmeaLearning, run.caseId);
      if (approval) approvedAtByCase.set(run.caseId, approval.at as SeriesTime);
    }
    return investigationRunRows({
      recorded,
      session: advisory.investigationRuns,
      cursor: scenario.cursor,
      approvedAtByCase,
      playingRunId: advisory.playingInvestigationRunId,
    });
  }, [
    traceState.status === 'ready' ? traceState.trace.recorded_at : undefined,
    advisory.investigationRuns,
    advisory.fmeaLearning,
    advisory.playingInvestigationRunId,
    scenario.cursor,
  ]);

  const tools = traceState.status === 'ready' ? investigationToolList(traceState.trace) : null;
  const ready = traceState.status === 'ready' || traceState.status === 'failed' || !recordedCase;
  return { runs, tools, ready };
}
